<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import { NButton, NEmpty, NFormItem, NInput, NPopconfirm, NSelect, NTime, useMessage } from 'naive-ui'
import { useRemindersStore, type ReminderScheduleInput } from '../stores/reminders'

// The recurring-schedule half of AdminReminders.vue (#1092) — the add/edit
// form and list of recurring schedules. Extracted so the parent stays
// under the 300-line hard limit; no behavior change from the inline
// version.

const store = useRemindersStore()
const message = useMessage()

const DEFAULT_FIRE_TIME = '09:00'
const CONFIRM_TRUNCATE_LENGTH = 50

const scheduleMessageRef = ref<InstanceType<typeof NInput> | null>(null)

const scheduleForm = reactive<ReminderScheduleInput>({
  message: '',
  recurrence_type: 'daily',
  fire_at_time: DEFAULT_FIRE_TIME,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  ends_on: null,
  day_of_week: null,
})
const editingScheduleId = ref<number | null>(null)
const savingSchedule = ref(false)

const RECURRENCE_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
]

const DAY_OF_WEEK_OPTIONS = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
]

async function startEditSchedule(id: number) {
  const s = store.schedules.find((schedule) => schedule.id === id)
  if (!s) return
  Object.assign(scheduleForm, {
    message: s.message,
    recurrence_type: s.recurrence_type,
    fire_at_time: s.fire_at_time,
    timezone: s.timezone,
    ends_on: s.ends_on ?? null,
    day_of_week: s.day_of_week ?? null,
  })
  editingScheduleId.value = id
  await nextTick()
  scheduleMessageRef.value?.focus()
}

function cancelEditSchedule() {
  editingScheduleId.value = null
  Object.assign(scheduleForm, {
    message: '',
    recurrence_type: 'daily',
    fire_at_time: DEFAULT_FIRE_TIME,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ends_on: null,
    day_of_week: null,
  })
}

async function saveSchedule() {
  if (!scheduleForm.message.trim()) {
    message.error('Message is required')
    return
  }
  if (!scheduleForm.fire_at_time) {
    message.error('Time is required')
    return
  }
  savingSchedule.value = true
  try {
    if (editingScheduleId.value !== null) {
      await store.updateSchedule(editingScheduleId.value, { ...scheduleForm })
      message.success('Schedule updated')
    } else {
      await store.createSchedule({ ...scheduleForm })
      message.success('Schedule created')
    }
    cancelEditSchedule()
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save schedule')
  } finally {
    savingSchedule.value = false
  }
}

async function deleteSchedule(id: number) {
  try {
    await store.deleteSchedule(id)
    message.success('Schedule deleted')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to delete schedule')
  }
}
</script>

<template>
  <div class="flex flex-col gap-3 mt-3">
    <!-- Add / edit schedule form -->
    <div class="bg-white/5 rounded-lg p-4 flex flex-col gap-3">
      <p class="text-sm font-medium text-white/80 m-0">
        {{ editingScheduleId !== null ? 'Edit schedule' : 'Add recurring schedule' }}
      </p>
      <NFormItem label="Message" :show-feedback="false">
        <NInput
          ref="scheduleMessageRef"
          v-model:value="scheduleForm.message"
          placeholder="e.g. Weekly standup review"
          @keydown.enter.prevent="saveSchedule"
        />
      </NFormItem>
      <div class="grid grid-cols-2 gap-3">
        <NFormItem label="Recurrence" :show-feedback="false">
          <NSelect
            v-model:value="scheduleForm.recurrence_type"
            :options="RECURRENCE_OPTIONS"
            @update:value="
              (v) => {
                if (v !== 'weekly') scheduleForm.day_of_week = null
              }
            "
          />
        </NFormItem>
        <NFormItem label="Time" :show-feedback="false">
          <input
            v-model="scheduleForm.fire_at_time"
            type="time"
            aria-label="Fire time (HH:MM)"
            class="w-full rounded px-3 py-1.5 bg-white/10 text-white text-sm border border-white/20 focus:outline-none focus:border-white/40"
          />
        </NFormItem>
      </div>
      <NFormItem v-if="scheduleForm.recurrence_type === 'weekly'" label="Day of week (optional)" :show-feedback="false">
        <NSelect
          v-model:value="scheduleForm.day_of_week"
          :options="DAY_OF_WEEK_OPTIONS"
          clearable
          placeholder="Any day (rolling 7 days)"
        />
      </NFormItem>
      <NFormItem label="End date (optional)" :show-feedback="false">
        <input
          v-model="scheduleForm.ends_on"
          type="date"
          aria-label="End date (optional, leave blank for indefinite)"
          class="w-full rounded px-3 py-1.5 bg-white/10 text-white text-sm border border-white/20 focus:outline-none focus:border-white/40"
        />
      </NFormItem>
      <p class="text-xs text-white/40 m-0">Timezone: {{ scheduleForm.timezone }}</p>
      <div class="flex gap-2">
        <NButton type="primary" :loading="savingSchedule" @click="saveSchedule">
          {{ editingScheduleId !== null ? 'Save changes' : 'Create schedule' }}
        </NButton>
        <NButton v-if="editingScheduleId !== null" quaternary @click="cancelEditSchedule">Cancel</NButton>
      </div>
    </div>

    <NEmpty v-if="store.schedules.length === 0" description="No recurring schedules yet" />
    <div v-for="s in store.schedules" :key="s.id" class="flex items-start gap-3 bg-white/5 rounded-lg p-3">
      <div class="flex-1 min-w-0">
        <p class="text-sm text-white m-0">{{ s.message }}</p>
        <p class="text-xs text-white/50 m-0 mt-0.5">
          {{ s.recurrence_type
          }}<span v-if="s.day_of_week !== null"> ({{ DAY_OF_WEEK_OPTIONS[s.day_of_week]?.label }}s)</span> at
          {{ s.fire_at_time }} · {{ s.timezone }}
          <span v-if="s.ends_on"> · ends {{ s.ends_on }}</span>
          <span v-if="s.next_fire_at"> · next: <NTime :time="new Date(s.next_fire_at)" /></span>
        </p>
      </div>
      <NButton size="tiny" quaternary :aria-label="`Edit schedule: ${s.message}`" @click="startEditSchedule(s.id)"
        >Edit</NButton
      >
      <NPopconfirm positive-text="Delete" negative-text="Cancel" @positive-click="deleteSchedule(s.id)">
        <template #trigger>
          <NButton size="tiny" quaternary type="error" :aria-label="`Delete schedule: ${s.message}`">Delete</NButton>
        </template>
        Delete schedule "{{
          s.message.length > CONFIRM_TRUNCATE_LENGTH ? s.message.slice(0, CONFIRM_TRUNCATE_LENGTH) + '…' : s.message
        }}" and its pending reminders?
      </NPopconfirm>
    </div>
  </div>
</template>
